import { Spinner, Text, XStack, YStack, View, isWeb } from 'tamagui'
import { useAdminPreview } from '@hooks/useAdminPreview'

import ActivityPage from '@components/Activity/ActivityPage'
import { LottieEffect } from '@components/LottieEffect'
import { HeroIcon } from '@components/HeroIcon'
import { MediaRender } from '@components/MediaRender'
import { ILesson } from '@dtos/Course'
import { useEnrollment } from '@hooks/useEnrollment'
import { useLessonNavigation } from '@hooks/useLessonNavigation'
import { useLessonProgress } from '@hooks/useLessonProgress'
import { usePremioReclameAquiPrompt } from '@hooks/usePremioReclameAquiPrompt'
import { mediaReportsPlaybackProgress } from '@utils/mediaPlaybackProgress'
import { PremioReclameAquiVoteModal } from '@components/PremioReclameAqui/VoteModal'
import { useMediaSize } from '@hooks/useMediaSize'
import { useScrollControl } from '@hooks/useScrollControl'
import { useForumTopicsCount } from '@services/api/graphql/comments'
import { useActiveCourseContentStore } from '@stores/useActiveCourseContentStore'
import { useLessonTabStore } from '@stores/useLessonTabStore'
import { useLocalSearchParams } from 'expo-router'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  GestureResponderEvent,
  Pressable,
  useWindowDimensions,
} from 'react-native'
import { ContentReportButton } from '@components/ContentReport/ContentReportButton'
import { NotSupported } from '../extra_contents/NotSupported'
import { CourseContentNavigation } from './CourseContentNavigation'
import { MediaRenderWrapper } from './MediaRenderWrapper'
import { TabsContentContainer } from './TabsContentContainer'
import { useHasFeature } from '@hooks/useThemeParams'
import { useDownloadContentAction } from '@hooks/useDownloadContentAction'
import { CONTENT_REPORT } from '@constants/features'
import { useTranslation } from 'react-i18next'

export interface IScreenProps {
  hasPreviousContent: boolean
  hasNextContent: boolean
  isCollapsibleOpen?: boolean
  toggleCollapsible?: () => void
  resetNextAndPreviousContent: () => void
  onLessonIdChange: (lessonId: string | number) => void
  /** Reavalia a navegação quando o servidor confirma a conclusão da aula. */
  onLessonCompleted?: () => void
  initialLesson?: ILesson | null
  isLoadingCourseContent?: boolean
}

export function MobileLayout({
  hasPreviousContent,
  hasNextContent,
  resetNextAndPreviousContent,
  onLessonIdChange,
  onLessonCompleted,
  initialLesson,
  isLoadingCourseContent = false,
}: IScreenProps) {
  const { t } = useTranslation('CourseDetails')
  const hasContentReport = useHasFeature(CONTENT_REPORT)
  const { currentTab, setCurrentTab } = useLessonTabStore()
  const { setIsScrollingContent } = useScrollControl()
  const { enrollment } = useEnrollment()
  const { height } = useWindowDimensions()
  const mediaSize = useMediaSize()
  const setActiveLessonId = useActiveCourseContentStore(
    (state) => state.setActiveLessonId,
  )
  const { courseId, enrollmentId, courseContentsId } = useLocalSearchParams()
  const [currentLesson, setCurrentLesson] = useState<ILesson | null>(null)

  useEffect(() => {
    if (initialLesson && !isLoadingCourseContent) {
      setCurrentLesson(initialLesson)
      onLessonIdChange(initialLesson.id)
      setActiveLessonId(initialLesson.id)
    }
  }, [
    initialLesson,
    isLoadingCourseContent,
    onLessonIdChange,
    setActiveLessonId,
  ])

  const { isPreview } = useAdminPreview()

  // Convite para votar no Prêmio RA 2026: entra quando o aluno conclui uma
  // aula, uma única vez por aluno e só durante a janela da votação. Em aula de
  // vídeo/áudio espera a reprodução acabar — no app a aula é dada como
  // concluída aos 75% e o convite entraria com o vídeo ainda tocando.
  const {
    isPromptOpen: isPremioRaPromptOpen,
    handleLessonCompleted: notifyPremioRaPrompt,
    handleMediaProgress: notifyPremioRaMediaProgress,
    handleClosePrompt: closePremioRaPrompt,
  } = usePremioReclameAquiPrompt({ lessonId: currentLesson?.id })

  const handleLessonCompleted = useCallback(() => {
    onLessonCompleted?.()
    notifyPremioRaPrompt({
      waitForMediaEnd: mediaReportsPlaybackProgress(currentLesson?.media),
    })
  }, [onLessonCompleted, notifyPremioRaPrompt, currentLesson?.media])

  const {
    isLessonCompleted,
    startMediaFrom,
    finishLessonButtonStyle,
    showLessonComplete,
    setShowLessonComplete,
    handleFinishLesson,
    handleUpdateProgress,
    updateEnrollmentProgress,
    currentLessonProgress,
  } = useLessonProgress({
    currentLesson,
    onLessonCompleted: handleLessonCompleted,
  })

  const onLessonChange = useCallback(
    (lesson: ILesson) => {
      setCurrentLesson(lesson)
      onLessonIdChange(lesson.id)
      setActiveLessonId(lesson.id)
    },
    [onLessonIdChange, setActiveLessonId],
  )

  // Activity conclui a aula via mutation própria (exam answers), já resolvida
  // quando este callback roda — seguro reavaliar a navegação no servidor.
  const handleActivityProgressUpdate = useCallback(() => {
    updateEnrollmentProgress()
    handleLessonCompleted()
  }, [updateEnrollmentProgress, handleLessonCompleted])

  const {
    handlePreviousLessonPress,
    handleNextLessonPress,
    isLoadingNextOrPreviousLesson,
  } = useLessonNavigation({
    currentLesson,
    enrollmentId: enrollment?.id,
    hasPreviousContent,
    hasNextContent,
    resetNextAndPreviousContent,
    onLessonChange,
  })

  const hasDescription = Boolean(currentLesson?.description)
  const hasExtraContent = Boolean((currentLesson?.extraMedias?.length ?? 0) > 0)
  const { hasTopics: hasComments } = useForumTopicsCount({
    courseContentId: currentLesson?.courseContentId
      ? String(currentLesson.courseContentId)
      : null,
  })
  const lessonWithoutContent =
    !hasDescription && !hasExtraContent && !hasComments
  const paddingBottom = lessonWithoutContent ? height * 0.125 : 0
  const extraMedia = useMemo(() => {
    return currentLesson?.media ?? null
  }, [currentLesson?.media])

  const { canDownload, downloadContent, isDownloading } =
    useDownloadContentAction(extraMedia)

  // Gate por presença de activity (e não por examQuestionsCount), pois o count
  // da lesson vem flaky do core. O ActivityPage busca as questões reais e trata
  // o estado vazio.
  const isActivity = !!currentLesson?.activity?.id

  const handlePress = (event: GestureResponderEvent) => {
    event.stopPropagation()
    setIsScrollingContent(false)
  }

  // No web o player de vídeo se dimensiona por aspect-ratio 16:9, então a
  // altura do card acompanha a largura da tela. O teto fixo de 750 foi feito
  // para celular; em tablet ele parava antes do fim do player e o conteúdo
  // seguinte subia por cima (o container tem `overflow: visible`).
  const fitsVideoHeight = isWeb && currentLesson?.media?.type === 'Video'

  return (
    <YStack w="100%" onPress={handlePress} role="button">
      <PremioReclameAquiVoteModal
        open={isPremioRaPromptOpen}
        onClose={closePremioRaPrompt}
      />
      <XStack
        gap="$spacing-inset-sm"
        $gtSm={{
          mt: '$spacing-inset-lg',
          marginHorizontal: 48,
        }}
        h="100%"
      >
        <YStack flex={1} w="100%">
          <YStack
            backgroundColor="$bg-surface"
            p="$spacing-inset-xs"
            gap="$spacing-inset-xs"
            paddingBottom="$spacing-inset-xs"
            maxHeight={!isActivity && !fitsVideoHeight ? 750 : 'unset'}
            $gtSm={{
              borderTopLeftRadius: '$border-radius-md',
              borderTopRightRadius: '$border-radius-md',
              p: '$spacing-inset-sm',
            }}
          >
            <XStack
              gap="$spacing-inset-xs"
              w="100%"
              alignItems="center"
              justifyContent="space-between"
            >
              {mediaSize === 'sm' && (
                <XStack w="100%">
                  <CourseContentNavigation
                    visibilityType="top"
                    onPreviousPress={handlePreviousLessonPress}
                    onNextPress={handleNextLessonPress}
                    hasPreviousContent={hasPreviousContent}
                    hasNextContent={hasNextContent}
                    isLoading={isLoadingNextOrPreviousLesson}
                  />
                </XStack>
              )}
            </XStack>

            {/* Player Controls */}

            {!isActivity && extraMedia && (
              <XStack alignItems="center" justifyContent="center" w="100%">
                <MediaRenderWrapper media={extraMedia}>
                  <MediaRender
                    media={extraMedia}
                    isImageVariant={extraMedia.type === 'Image'}
                    thumbnailUrl={currentLesson?.coverImage?.cdnFileUrl}
                    startMediaFrom={startMediaFrom}
                    updateLessonProgress={({ progress, currentVideoTime }) => {
                      handleUpdateProgress(progress, currentVideoTime)
                      notifyPremioRaMediaProgress(progress)
                    }}
                    progress={currentLessonProgress?.progress}
                    fileUrl={currentLesson?.media?.fileUrl ?? ''}
                    isLoading={isLoadingCourseContent}
                  >
                    {/* Fallback para conteúdo não renderizável (ex.: .docx):
                        sem isso o player fica em branco no mobile. */}
                    <NotSupported
                      media={extraMedia}
                      fileUrl={extraMedia.fileUrl ?? undefined}
                    />
                  </MediaRender>
                </MediaRenderWrapper>
              </XStack>
            )}
            {isActivity && (
              <ActivityPage
                activityId={currentLesson?.activity?.id?.toString() ?? ''}
                courseId={courseId?.toString() ?? ''}
                enrollmentId={enrollmentId?.toString() ?? ''}
                onPreviousPress={handlePreviousLessonPress}
                onNextPress={handleNextLessonPress}
                hasPreviousContent={hasPreviousContent}
                hasNextContent={hasNextContent}
                isLoadingNextOrPreviousLesson={isLoadingNextOrPreviousLesson}
                progressCardId={enrollment?.progressCard?.id?.toString() ?? ''}
                updateEnrollmentProgress={handleActivityProgressUpdate}
              />
            )}
            {/* Content General Information */}
            {!isActivity && (
              <YStack
                gap="$spacing-inset-sm"
                paddingHorizontal="$spacing-inset-xxxs"
              >
                <YStack gap="$spacing-inset-sm">
                  <Text
                    color="$text-primary"
                    fontSize="$font-size-lg"
                    fontFamily="$mulishBold"
                    fontWeight="700"
                    numberOfLines={2}
                  >
                    {currentLesson?.title || ''}
                  </Text>

                  <XStack alignItems="center">
                    <XStack
                      alignItems="center"
                      justifyContent="space-between"
                      gap="$spacing-inset-sm"
                      flex={1}
                    >
                      <XStack alignItems="center" gap="$spacing-inset-sm">
                        <View
                          position="relative"
                          // Visão do produtor não conclui aula: o servidor
                          // recusa a escrita, então o botão não aparece.
                          display={isPreview ? 'none' : 'flex'}
                        >
                          <Pressable
                            testID="finish-lesson-button"
                            onPress={handleFinishLesson}
                            disabled={isLessonCompleted}
                          >
                            <YStack
                              gap="$spacing-inset-quarck"
                              alignItems="center"
                              p="$spacing-inset-nano"
                            >
                              <HeroIcon
                                icon={finishLessonButtonStyle.icon}
                                size={21}
                                color={finishLessonButtonStyle.iconColor}
                              />
                              <Text
                                color="$text-primary"
                                fontSize="$font-size-sm"
                                fontFamily="$mulishRegular"
                                lineHeight={24}
                              >
                                {finishLessonButtonStyle.label}
                              </Text>
                            </YStack>
                          </Pressable>
                          <View
                            testID="lesson-complete-overlay"
                            position="absolute"
                            top={0}
                            left={0}
                            right={0}
                            bottom={30}
                            // Sem isto a View cobre o ícone do botão de
                            // concluir e engole o toque — só o label, fora
                            // da faixa absoluta, continuava clicável.
                            pointerEvents="none"
                          >
                            {showLessonComplete && (
                              <LottieEffect
                                source={require('@assets/animations/lesson_completed.json')}
                                onComplete={() => setShowLessonComplete(false)}
                              />
                            )}
                          </View>
                        </View>

                        {/* Mesma marcação do botão de concluir (ícone em cima,
                            label embaixo) para os dois saírem idênticos. */}
                        {canDownload && (
                          <Pressable
                            testID="download-content-button"
                            onPress={downloadContent}
                            disabled={isDownloading}
                          >
                            <YStack
                              gap="$spacing-inset-quarck"
                              alignItems="center"
                              p="$spacing-inset-nano"
                            >
                              {isDownloading ? (
                                <Spinner size="small" color="$accent" />
                              ) : (
                                <HeroIcon
                                  icon={['fal', 'download']}
                                  size={21}
                                  color="$accent"
                                />
                              )}
                              <Text
                                color="$text-primary"
                                fontSize="$font-size-sm"
                                fontFamily="$mulishRegular"
                                lineHeight={24}
                              >
                                {isDownloading
                                  ? t('download.loading')
                                  : t('download.label')}
                              </Text>
                            </YStack>
                          </Pressable>
                        )}
                      </XStack>
                      {hasContentReport && (
                        <View marginRight="$spacing-inset-xs">
                          <ContentReportButton
                            schoolProductId={String(
                              enrollment?.schoolProduct?.id,
                            )}
                            courseId={String(courseId)}
                            lessonId={String(currentLesson?.id)}
                            courseContentId={String(courseContentsId)}
                          />
                        </View>
                      )}
                    </XStack>
                    {mediaSize === 'xs' && (
                      <View bottom={2}>
                        <CourseContentNavigation
                          onPreviousPress={handlePreviousLessonPress}
                          onNextPress={handleNextLessonPress}
                          hasPreviousContent={hasPreviousContent}
                          hasNextContent={hasNextContent}
                          isLoading={isLoadingNextOrPreviousLesson}
                        />
                      </View>
                    )}
                  </XStack>
                </YStack>
              </YStack>
            )}
          </YStack>
          <YStack paddingBottom={paddingBottom}>
            <TabsContentContainer
              currentLesson={currentLesson}
              currentTab={currentTab}
              onTabChange={setCurrentTab}
              description={currentLesson?.description ?? ''}
              extraMedias={currentLesson?.extraMedias ?? []}
            />
          </YStack>
        </YStack>
      </XStack>
    </YStack>
  )
}
